<template>
  <div>
    <table
      v-if="webAuthnKeys.length"
      :class="$style.webAuthnKeysTable"
    >
      <thead>
        <tr>
          <th :class="[$style.th, $style.credentialId]">
            Credential ID
          </th>
          <th :class="[$style.th, $style.registrationDate]">
            Register Date
          </th>
          <th :class="[$style.th, $style.name]">
            Display Label
          </th>
          <th :class="[$style.th, $style.enabled]">
            Enabled
          </th>
          <th :class="[$style.th, $style.actions]">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="webAuthnKey in webAuthnKeys"
          :key="webAuthnKey.id"
        >
          <td :class="[$style.td, $style.credentialId]">
            <span
              :class="$style.credentialIdContent"
              :title="webAuthnKey.credentialId"
            >
              {{ webAuthnKey.credentialId }}
            </span>
          </td>
          <td :class="[$style.td, $style.registrationDate]">
            {{ formatDate(webAuthnKey.registrationDate) }}
          </td>
          <td :class="[$style.td, $style.name]">
            <EditableInputWithIcon
              attribute="name"
              :config="{ validate: 'max:255' }"
              :editViewClassName="$style.nameEditView"
              :handleSubmit="updateName"
              :item="webAuthnKey"
            >
              <span
                :class="$style.nameContent"
                :title="webAuthnKey.name"
              >
                {{ webAuthnKey.name || dataNotAvailable }}
              </span>
            </EditableInputWithIcon>
          </td>
          <td :class="[$style.td, $style.enabled]">
            <PrettyToggle
              :config="{value: webAuthnKey.enabled}"
              :handleToggleChange="value => updateEnabled(webAuthnKey, value)"
            />
          </td>
          <td :class="[$style.td, $style.actions]">
            <IconButton
              title="Delete"
              @click="() => deleteKey(webAuthnKey)"
            >
              <TrashIcon :class="$style.deleteIcon" />
            </IconButton>
          </td>
        </tr>
      </tbody>
    </table>
    <div :class="$style.actionButtonWrapper">
      <FlatActionButton
        :action="addKey"
        :buttonClass="['btnDefault', 'btnCTA']"
        :disabled="webAuthnNotSupported"
        text="add key"
      />
      <MessageContainer
        v-if="webAuthnNotSupported"
        :class="$style.actionButtonMessage"
      >
        Security Key authentication is not supported by this browser
      </MessageContainer>
    </div>
  </div>
</template>
<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import { webAuthnSupported } from 'client/util/FeatureSupport';
import ConfirmationModal from 'client/components/Modal/ModalComponents/ConfirmationModal.vue';
import EditableInputWithIcon from 'client/components/ItemTable/BodyComponents/EditableInputWithIcon.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IconButton from 'client/components/Buttons/IconButton.vue';
import MessageContainer from 'client/components/MessageContainer/MessageContainer.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PrettyToggle from 'client/components/FormValidation/PrettyToggle';
import RegisterWebAuthnKeyModal from 'client/systemusers/UserDetails/RegisterWebAuthnKeyModal.vue';
import TrashIcon from 'public/img/icons/trash.svg?inline';
import moment from 'moment';

const { ItemTypeConsts, StoreModelMap } = Constants;
const { dataNotAvailable, dateFormat } = Constants.default;
const systemUserWebAuthKeysModel = StoreModelMap[ItemTypeConsts.systemUserWebAuthnKeys];

/**
 * @todo This component behaves similar to a ListEditor but is not a list, but rather it is a
 * table. This is clearly indicated in the prototypes where there is a distinct header with aligned
 * columns. Can the existing ListEditor be reused here? Or should new funtionality be introduced
 * because the WebAuthn keys are displayed in a table?
 * @see https://jumpcloud.invisionapp.com/share/2FVCF1VTZ7W#/screens/398424210
 */
export default {
  name: 'WebAuthnKeysLegacy',

  components: {
    EditableInputWithIcon,
    FlatActionButton,
    IconButton,
    MessageContainer,
    PrettyToggle,
    TrashIcon,
  },

  inject: ['$validator'],

  computed: {
    ...mapState(systemUserWebAuthKeysModel, {
      webAuthnKeys: state => state.item.webAuthnKeys,
    }),
    webAuthnNotSupported() {
      return !webAuthnSupported();
    },
  },

  created() {
    this.dataNotAvailable = dataNotAvailable;
  },

  methods: {
    ...mapActions(systemUserWebAuthKeysModel, [
      'deleteWebAuthnKey',
      'updateWebAuthnKey',
    ]),

    addKey() {
      ModalEventBus.open({
        component: RegisterWebAuthnKeyModal,
      });
    },

    deleteKey(webAuthnKey) {
      const componentProps = {
        headerText: 'Delete WebAuthn Security Key',
        bodyText: 'Are you sure? If the user is required to use MFA and WebAuthn is the only enabled factor, deleting the user\'s last remaining key locks the user out of the User Portal.',
        continueText: 'confirm',
        handleCancel: () => ModalEventBus.close(),
        handleContinue: () => {
          ModalEventBus.close();
          this.deleteWebAuthnKey(webAuthnKey);
        },
        mainActionAttention: true,
        showClose: true,
      };

      ModalEventBus.open({
        isLargeModal: false,
        component: ConfirmationModal,
        componentProps,
      });
    },

    formatDate(date) {
      return moment(date).format(dateFormat);
    },

    updateEnabled(webAuthnKey, value) {
      this.updateWebAuthnKey({
        webAuthnKey,
        key: 'enabled',
        value,
      });
    },

    updateName(webAuthnKey, value) {
      this.updateWebAuthnKey({
        webAuthnKey,
        key: 'name',
        value,
      });
    },
  },
};
</script>
<style module>
@value (max-width-x-large) from '../../css/breakpoints.css';

.actionButtonWrapper {
  display: flex;
  flex-direction: row;
}

.actionButtonMessage {
  flex-grow: 0;
  margin-bottom: 0;
  margin-left: 10px;
}

.webAuthnKeysTable {
  width: 100%;
}

.webAuthnKeysTable thead {
  border-bottom: 1px solid #d3dde4;
}

.webAuthnKeysTable thead th {
  padding: 0 0.625rem 0.625rem 0;
  white-space: nowrap;
}

.webAuthnKeysTable tbody tr {
  height: 4rem;
}

.webAuthnKeysTable thead th:last-child,
.webAuthnKeysTable tbody td:last-child {
  text-align: center;
}

.credentialId,
.name {
  max-width: 100px;
  overflow: hidden;
  width: 30%;
}

/*
 * TEMP: Remove this when the PATCH request supports the enabled field!
 * SEE: https://jumpcloud.atlassian.net/browse/MFA-188
 */
.name {
  /**
  * negative top margin to account for font leading space
  * see EditableInputWithIcon for example usage
  */

  --iconTopOffset: -0.5em;
  --iconMaxWidth: 1.25em;
  --iconLeftOffset: 0.5em;

  width: 45%;
}

.deleteIcon {
  width: 1.5rem;
}

.deleteIcon path:hover {
  fill: var(--jcError700);
}

@media (min-width: max-width-x-large) {
  .credentialId,
  .name {
    max-width: 150px;
  }
}

@media (min-width: 1600px) {
  .credentialId,
  .name {
    max-width: 250px;
  }
}

.registrationDate,
.enabled {
  width: 15%;
}

/*
 * TEMP: Remove this when the PATCH request supports the enabled field!
 * SEE: https://jumpcloud.atlassian.net/browse/MFA-188
 */
.enabled {
  display: none;
}

.actions {
  text-align: center;
  width: 10%;
}

.credentialIdContent,
.nameContent {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nameEditView {
  width: 80%;
}
</style>
